// REACT
import React, { useState, useRef, SyntheticEvent, useContext } from 'react';
import { useTranslation } from 'react-i18next';

// YARN
import { toast } from 'react-toastify';
import { ThemeProvider } from '@mui/material/styles';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  TextField,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import DoneIcon from '@mui/icons-material/Done';
import { Close } from '@mui/icons-material';

// SERVICES
import {
  inputRequired,
  inputVerifyMail,
  inputStringMinLengthValidator,
  inputNumberStrictLengthValidatorOrNull,
  inputNumberMinMaxLengthValidatorOrNull,
} from 'components/generics/Validators/validator';
import {
  addExploitation,
  addFarmer,
  removeFarmer,
  removeExploitation,
  updateExploitation,
} from 'services/API/Secteur';
import {
  checkTypeStringRegexDatagrid,
  displayErrorMessage,
  checkDecimalNegativeDatagrid,
} from 'utils/tools_functions';
import { validateFormData } from 'components/generics/Validators/form.validate';
import { customButtonTheme } from 'assets/styles/themes/generic_button_mui';
import { customDialogTheme } from 'assets/styles/themes/Dialog/generic_dialog_mui';
import LoadingScreen from 'components/generics/loadingScreen';
import { customDialogAddEditCommonTheme } from 'assets/styles/themes/Dialog/generic_dialog_add_edit_parcel';

// INTERFACES
import ISector from 'components/generics/Interface/Api/Response/Structures/ISector';
import { IErrorResponse } from 'components/generics/Interface/Commons/IErrorResponse';
import { LoadingContext } from 'utils/context';
import { ILoadingContext } from 'components/generics/Interface/Context/ILoadingContext';

import './exploitations.style.scss';

type FormDataType = {
  id?: number;
  name: string;
  code: string | null;
  irrigability: number;
  farmers?: FormDataFarmerType[];
};

type FormDataFarmerType = {
  id?: number;
  firstname: string;
  lastname: string;
  email: string;
  role: Array<string>;
  cooperative: number;
  sectors: Array<number>;
  exploitations: Array<number>;
};

const DEFAULT_FORM: FormDataType = {
  name: '',
  code: '',
  irrigability: 0,
};

const DEFAULT_FARMER: FormDataFarmerType = {
  firstname: '',
  lastname: '',
  email: '',
  role: ['ROLE_FARMER'],
  cooperative: 0,
  sectors: [],
  exploitations: [],
};

type AddExploitationModalParams = {
  modal: boolean;
  setModal: React.Dispatch<React.SetStateAction<boolean>>;
  onSubmitSuccess: () => void;
  secteur: ISector | null;
  loadDataSector: (sectorId: number) => void;
  choosenStructureId: number | null;
  edit?: FormDataType | null;
  isEdit: boolean;
};

const AddExploitationModal = ({
  modal,
  setModal,
  onSubmitSuccess,
  secteur,
  loadDataSector,
  choosenStructureId,
  edit,
  isEdit,
}: AddExploitationModalParams): React.JSX.Element => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormDataType>(isEdit ? edit! : DEFAULT_FORM);
  const [currentFarmers, setCurrentFarmers] = useState(isEdit ? edit?.farmers : []);
  const [newFarmers, setNewFarmers] = useState<FormDataFarmerType[]>([]);
  const [addFarmers, setAddFarmers] = useState<FormDataFarmerType>(DEFAULT_FARMER);
  const [removeId, setRemoveId] = useState<number[]>([]);
  const { loading } = useContext(LoadingContext) as ILoadingContext;
  const [errorsFormDefault, setErrorsFormDefault] = useState({
    formError: false,
    name: {
      message: '',
      validator: [inputRequired, inputStringMinLengthValidator(2)],
    },
    code: {
      message: '',
      validator: [
        isEdit
          ? inputNumberMinMaxLengthValidatorOrNull(1, 30)
          : inputNumberStrictLengthValidatorOrNull(9),
      ],
    },
  });
  const [errorsFormFarmer, setErrorsFormFarmer] = useState({
    formError: false,
    firstname: {
      message: '',
      validator: [inputRequired],
    },
    lastname: {
      message: '',
      validator: [inputRequired],
    },
    email: {
      message: '',
      validator: [inputVerifyMail],
    },
  });
  const ref = useRef<HTMLDivElement>(null);
  const choosenStructureIdChoose = Number(localStorage.getItem('cooperative'));
  const [isSuspendOpen, setIsSuspendOpen] = useState(false);

  const addFarmerHandle = () => {
    if (addFarmers !== DEFAULT_FARMER) {
      errorsFormFarmer.formError = false;
      setErrorsFormFarmer({ ...validateFormData(addFarmers, errorsFormFarmer) });
      if (!errorsFormFarmer.formError) {
        const newFarmersArr = newFarmers;
        newFarmersArr.push(addFarmers);
        setNewFarmers(newFarmersArr);
        setAddFarmers(DEFAULT_FARMER);
      }
    }
  };

  const deleteCurrentFarmer = (farmer: FormDataFarmerType) => {
    const newDeleteArray = removeId;
    newDeleteArray.push(farmer.id ?? 0);
    setRemoveId(newDeleteArray);
    const newCurrentFarmers = currentFarmers?.filter(
      (currentFarmer) => currentFarmer.id !== farmer?.id,
    );
    setCurrentFarmers(newCurrentFarmers);
  };

  const deleteNewFarmer = (index: number) => {
    const arr: FormDataFarmerType[] = [];
    for (let i = 0; i < newFarmers.length; i++) {
      if (i !== index) {
        arr.push(newFarmers[i]);
      }
    }
    setNewFarmers(arr);
  };

  const checkDisabledFarmerButton = () => {
    return !(addFarmers.firstname && addFarmers.lastname && addFarmers.email);
  };

  const handleOnSubmit = (e: SyntheticEvent) => {
    if (e) e.preventDefault();

    errorsFormDefault.formError = false;
    errorsFormFarmer.formError = false;

    // Validate formData
    setErrorsFormDefault({ ...validateFormData(formData, errorsFormDefault) });
    //change empty code string to null before call API because API doesn't accept empty string
    const exploitationData = {
      ...formData,
      code: formData.code === '' ? null : formData.code,
      sector: secteur?.id,
    };

    // Validate each new farmer
    let allFarmersValid = true;
    newFarmers.forEach((farmer) => {
      const farmerErrors = validateFormData(farmer, errorsFormFarmer);
      if (farmerErrors.formError) {
        allFarmersValid = false;
        setErrorsFormFarmer({ ...farmerErrors });
      }
    });

    if (!errorsFormDefault.formError && allFarmersValid) {
      const addOrUpdateFarmers = (exploitationId: number) => {
        const updatedFarmers = newFarmers.map((farmer) => ({
          ...farmer,
          sectors: [...farmer.sectors, secteur?.id],
          exploitations: [...farmer.exploitations, exploitationId],
          cooperative: choosenStructureIdChoose ? choosenStructureIdChoose : choosenStructureId,
        }));

        return Promise.all(
          updatedFarmers.map((farmer) =>
            addFarmer(farmer)
              .then(() => {
                toast.success(
                  t('success.farmerCreated', {
                    firstname: farmer.firstname,
                    lastname: farmer.lastname,
                  }),
                );
              })
              .catch((err) => {
                const apiError = err as IErrorResponse;
                if (apiError?.response?.status === 409) {
                  toast.error(displayErrorMessage('ERR_ADD_EXISTING_EMAIL', farmer as any));
                } else {
                  toast.error(err.response.data.message);
                }
              }),
          ),
        );
      };

      if (isEdit) {
        removeId.forEach((id) => {
          removeFarmer(id)
            .then(() => {
              toast.success(t('success.farmerDeleted'));
              loadDataSector(secteur?.id ?? 0);
            })
            .catch((err) => {
              toast.error(err.message);
            });
        });

        updateExploitation(exploitationData, formData.id)
          .then(() => {
            toast.success(t('success.exploitationUpdated', { name: formData.name }));
            return addOrUpdateFarmers(formData.id ?? 0);
          })
          .then(() => {
            loadDataSector(secteur?.id ?? 0);
            onSubmitSuccess();
          })
          .catch((err) => {
            if (err?.response?.data.hasOwnProperty('data')) {
              err.response.data.data.codeExploitation = formData.code;
              toast.error(
                displayErrorMessage(err.response.data?.data?.code, err.response.data?.data),
              );
            } else {
              toast.error(displayErrorMessage(err?.response?.data?.code));
            }
          });
      } else {
        addExploitation(exploitationData)
          .then((response) => {
            const exploitationId = response.data.data.id;
            toast.success(t('success.exploitationCreated', { name: formData.name }));
            return addOrUpdateFarmers(exploitationId);
          })
          .then(() => {
            loadDataSector(secteur?.id ?? 0);
            onSubmitSuccess();
          })
          .catch((err) => {
            if (err?.response?.data.hasOwnProperty('data')) {
              err.response.data.data.codeExploitation = formData.code;
              toast.error(
                displayErrorMessage(err.response.data?.data?.code, err.response.data?.data),
              );
            } else {
              toast.error(displayErrorMessage(err?.response?.data?.code));
            }
          });
      }
    }
  };

  const updateNewFarmer = (key: string, value: string) =>
    setAddFarmers({ ...addFarmers, [key]: value });

  const updateForm = (key: string, value: string) => {
    setFormData({ ...formData, [key]: value });
  };

  const handleSuspend = () => {
    removeExploitation(formData.id)
      .then(() => {
        if (localStorage.getItem('exploitation') === formData?.id?.toString()) {
          localStorage.removeItem('exploitation');
        }
        toast.success(t('success.exploitationDeleted', { name: formData.name }));
        loadDataSector(secteur?.id ?? 0);
      })
      .catch((err) => {
        toast.error(displayErrorMessage(err?.response?.data?.code));
      });
    onSubmitSuccess();
  };

  return (
    <div>
      <ThemeProvider theme={customDialogAddEditCommonTheme}>
        <Dialog
          maxWidth="md"
          fullWidth
          open={modal}
          ref={ref}
          onClose={() => setModal(false)}
          id="add_exploitation_dialog"
        >
          <div className="add_exploitation_container">
            <LoadingScreen open={loading}></LoadingScreen>
            <Box className="modal_header">
              <DialogTitle>
                {isEdit ? t('title.editExploitation') : t('title.addExploitation')}
              </DialogTitle>
              <ClearIcon className="close-icon" onClick={() => setModal(false)} />
            </Box>
            <DialogContent>
              <fieldset className="add_exploitation_fieldset">
                <legend>{t('label.exploitation')}</legend>
                <Box id="add_name" className="add_exploitation_container__box">
                  <InputLabel id="nameLabel" htmlFor="name">
                    {t('common.name')}
                    <span className="label_asterisk">*</span>
                  </InputLabel>
                  <TextField
                    id="name"
                    value={formData.name}
                    onChange={(e) => {
                      updateForm('name', checkTypeStringRegexDatagrid(e.target.value));
                    }}
                    placeholder={t('placeholder.exploitationName')}
                    disabled={loading}
                    error={errorsFormDefault?.name.message ? true : false}
                    helperText={errorsFormDefault?.name.message}
                  />
                </Box>
                <Box className="add_exploitation_container__box">
                  <InputLabel id="codeLabel" htmlFor="code">
                    {t('column.pacageNumber')}
                  </InputLabel>
                  <TextField
                    id="code"
                    onChange={(e) => {
                      updateForm('code', checkTypeStringRegexDatagrid(e.target.value));
                    }}
                    value={formData.code}
                    placeholder="123456789"
                    disabled={loading}
                    error={Boolean(errorsFormDefault?.code.message)}
                    helperText={errorsFormDefault?.code.message}
                  />
                </Box>
                <Box className="add_exploitation_container__box">
                  <InputLabel id="irrigabilityLabel" htmlFor="irrigability">
                    {t('form.irrigationCapacity')}{' '}
                  </InputLabel>
                  <TextField
                    id="irrigability"
                    onChange={(e) => {
                      updateForm('irrigability', checkDecimalNegativeDatagrid(e.target.value));
                    }}
                    value={formData.irrigability}
                    placeholder="0"
                    disabled={loading}
                  />
                </Box>
                <Box>
                  <InputLabel id="sectorLabel" htmlFor="secteur">
                    {t('label.sector')}
                  </InputLabel>
                  <TextField
                    id="secteur"
                    value={secteur?.name}
                    placeholder={secteur?.name}
                    disabled={true}
                  />
                </Box>
              </fieldset>
              <fieldset className="add_farmer_fieldset">
                <legend>
                  {t('form.farmer')}{' '}
                  <ThemeProvider theme={customButtonTheme}>
                    <Button
                      id="add_farmer_button"
                      onClick={addFarmerHandle}
                      disabled={checkDisabledFarmerButton()}
                    >
                      {t('common.add')}
                    </Button>
                  </ThemeProvider>
                </legend>
                <Box className="add-farmer-list">
                  <Box>
                    {currentFarmers &&
                      currentFarmers.length > 0 &&
                      currentFarmers.map((farmer) => (
                        <div key={farmer.id}>
                          <Button
                            variant="contained"
                            onClick={() => deleteCurrentFarmer(farmer)}
                            disabled={loading}
                            endIcon={<Close width={13} height={13} />}
                          >
                            {`${farmer.firstname} ${farmer.lastname}`}
                          </Button>
                        </div>
                      ))}
                    {newFarmers.map((farmer, index) => (
                      <div key={index}>
                        <Button
                          variant="contained"
                          onClick={() => deleteNewFarmer(index)}
                          disabled={loading}
                          endIcon={<Close width={13} height={13} />}
                        >
                          {`${farmer.firstname} ${farmer.lastname}`}
                        </Button>
                      </div>
                    ))}
                  </Box>
                </Box>
                <Box>
                  <InputLabel id="firtnameLabel" htmlFor="firstname">
                    {t('label.prenom')}
                  </InputLabel>
                  <TextField
                    id="firstname"
                    value={addFarmers.firstname}
                    onChange={(e) => {
                      updateNewFarmer('firstname', checkTypeStringRegexDatagrid(e.target.value));
                    }}
                    placeholder={t('placeholder.farmerFirstname')}
                    disabled={loading}
                  />
                </Box>
                <Box>
                  <InputLabel id="lastnameLabel" htmlFor="lastname">
                    {t('form.lastname')}
                  </InputLabel>
                  <TextField
                    id="lastname"
                    value={addFarmers.lastname}
                    onChange={(e) => {
                      updateNewFarmer('lastname', checkTypeStringRegexDatagrid(e.target.value));
                    }}
                    placeholder={t('placeholder.farmerLastname')}
                    disabled={loading}
                  />
                </Box>
                <Box>
                  <InputLabel id="emailLabel" htmlFor="email">
                    {t('label.email')}
                  </InputLabel>
                  <TextField
                    id="email"
                    value={addFarmers.email}
                    onChange={(e) => {
                      updateNewFarmer('email', e.target.value);
                    }}
                    placeholder={t('placeholder.farmerEmail')}
                    disabled={loading}
                    error={Boolean(errorsFormFarmer?.email.message)}
                    helperText={errorsFormFarmer?.email.message}
                  />
                </Box>
              </fieldset>
            </DialogContent>
            <div className="text_helper_exploitation">
              <div>
                <span className="label_asterisk">*</span> {t('message.required')}
              </div>
            </div>
            <DialogActions>
              <ThemeProvider theme={customButtonTheme}>
                {isEdit && (
                  <Button
                    id="custom-delete-button"
                    type="submit"
                    disabled={loading}
                    onClick={(e) => {
                      e.preventDefault();
                      setIsSuspendOpen(true);
                    }}
                  >
                    {t('button.deleteExploitation')}
                  </Button>
                )}
                <Box>
                  <Button id="add_exploitation_button" onClick={handleOnSubmit}>
                    {isEdit ? t('common.update') : t('common.add')}
                  </Button>
                </Box>
              </ThemeProvider>
            </DialogActions>
            {isSuspendOpen && (
              <ThemeProvider theme={customDialogTheme}>
                <Dialog open={isSuspendOpen}>
                  <DialogTitle>{t('common.warning')}</DialogTitle>
                  <DialogContent dividers>
                    <h3>{t('title.deleteExploitation')}</h3>
                    <p>{t('message.confirmDeleteExploitation')}</p>
                    <p>
                      <strong>{isEdit ? `${formData.name}` : ''}</strong>. <br />{' '}
                      {t('common.continueQuestion')}
                    </p>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleSuspend}>
                      {t('common.delete')} <DoneIcon />
                    </Button>
                    <Button onClick={() => setIsSuspendOpen(false)}>
                      {t('common.no')} <ClearIcon />
                    </Button>
                  </DialogActions>
                </Dialog>
              </ThemeProvider>
            )}
          </div>
        </Dialog>
      </ThemeProvider>
    </div>
  );
};

export default AddExploitationModal;
